import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiRequest } from '../../authConfig.js';
import {
  adminGetUsers,
  adminGetPermissions,
  adminUpsertPermission,
  adminDeletePermission,
  adminGetWorkspacesReports,
  adminGetDatasetRoles,
} from '../../api.js';
import styles from './AdminTable.module.css';
import permStyles from './Permissions.module.css';

const EMPTY_FORM = { workspaceId: '', reportId: '', reportName: '', rlsRole: '', rlsUsername: '' };

export default function Permissions() {
  const { instance, accounts } = useMsal();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [workspacesError, setWorkspacesError] = useState(null);
  const [rlsRoles, setRlsRoles] = useState([]);
  const [rlsRolesLoading, setRlsRolesLoading] = useState(false);

  const getToken = useCallback(async () => {
    const resp = await instance.acquireTokenSilent({ ...apiRequest, account: accounts[0] });
    return resp.idToken;
  }, [instance, accounts]);

  // Load user list on mount
  useEffect(() => {
    getToken()
      .then((token) => adminGetUsers(token))
      .then(setUsers)
      .catch(console.error);
  }, [getToken]);

  // Load permissions when user changes
  useEffect(() => {
    if (!selectedUserId) { setPermissions([]); return; }
    setLoading(true);
    getToken()
      .then((token) => adminGetPermissions(token, selectedUserId))
      .then(setPermissions)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedUserId, getToken]);

  // Load workspaces+reports when modal opens
  useEffect(() => {
    if (!showModal) return;
    setWorkspacesLoading(true);
    setWorkspacesError(null);
    getToken()
      .then((token) => adminGetWorkspacesReports(token))
      .then(setWorkspaces)
      .catch((err) => setWorkspacesError(err.message))
      .finally(() => setWorkspacesLoading(false));
  }, [showModal, getToken]);

  // Derive the available reports for the selected workspace
  const selectedWorkspace = workspaces.find((w) => w.workspaceId === form.workspaceId);
  const availableReports = selectedWorkspace?.reports ?? [];

  function handleWorkspaceChange(workspaceId) {
    setForm((f) => ({ ...f, workspaceId, reportId: '', reportName: '', rlsRole: '' }));
    setRlsRoles([]);
  }

  async function handleReportChange(reportId) {
    const report = availableReports.find((r) => r.reportId === reportId);
    setForm((f) => ({ ...f, reportId, reportName: report?.reportName ?? '', rlsRole: '' }));
    setRlsRoles([]);
    if (report?.datasetId) {
      setRlsRolesLoading(true);
      try {
        const token = await getToken();
        const roles = await adminGetDatasetRoles(token, {
          workspaceId: form.workspaceId,
          datasetId: report.datasetId,
        });
        setRlsRoles(roles);
      } catch {
        setRlsRoles([]);
      } finally {
        setRlsRolesLoading(false);
      }
    }
  }

  async function handleAddPermission(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = await getToken();
      await adminUpsertPermission(token, { userId: selectedUserId, ...form });
      const updated = await adminGetPermissions(token, selectedUserId);
      setPermissions(updated);
      setShowModal(false);
      setForm(EMPTY_FORM);
    } catch (err) {
      alert(`Erro: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(reportId) {
    if (!window.confirm('Confirmar remoção desta permissão?')) return;
    try {
      const token = await getToken();
      await adminDeletePermission(token, { userId: selectedUserId, reportId });
      setPermissions((prev) => prev.filter((p) => p.reportId !== reportId));
    } catch (err) {
      alert(`Erro: ${err.message}`);
    }
  }

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.title}>Permissões</h1>
      </div>

      <div className={permStyles.userSelect}>
        <label htmlFor="user-select">Seleccionar utilizador:</label>
        <select
          id="user-select"
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          className={permStyles.select}
        >
          <option value="">-- Escolher utilizador --</option>
          {users.map((u) => (
            <option key={u.userId} value={u.userId}>
              {u.displayName || u.email}
            </option>
          ))}
        </select>
      </div>

      {selectedUserId && (
        <>
          <div className={permStyles.addRow}>
            <button className={styles.addBtn} onClick={() => setShowModal(true)}>
              + Adicionar permissão
            </button>
          </div>

          {loading ? (
            <div className={styles.loading}>A carregar permissões...</div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Relatório</th>
                    <th>Workspace</th>
                    <th>RLS Role</th>
                    <th>RLS Username</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {permissions.map((p) => (
                    <tr key={p.reportId}>
                      <td>
                        <strong>{p.reportName}</strong>
                        <br />
                        <small className={styles.mono}>{p.reportId}</small>
                      </td>
                      <td className={styles.mono}>{p.workspaceId}</td>
                      <td>{p.rlsRole || '—'}</td>
                      <td>{p.rlsUsername || '—'}</td>
                      <td>
                        <button className={styles.deleteBtn} onClick={() => handleDelete(p.reportId)}>
                          Remover
                        </button>
                      </td>
                    </tr>
                  ))}
                  {permissions.length === 0 && (
                    <tr>
                      <td colSpan={5} className={styles.emptyRow}>
                        Sem permissões atribuídas.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className={permStyles.overlay} onClick={() => setShowModal(false)}>
          <div className={permStyles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={permStyles.modalTitle}>Adicionar permissão</h2>

            {workspacesLoading && <p style={{ color: '#888' }}>A carregar workspaces...</p>}
            {workspacesError && (
              <p style={{ color: '#e53e3e', fontSize: '0.85rem' }}>Erro: {workspacesError}</p>
            )}

            <form onSubmit={handleAddPermission} className={permStyles.form}>
              <label>
                Workspace *
                <select
                  required
                  value={form.workspaceId}
                  onChange={(e) => handleWorkspaceChange(e.target.value)}
                  className={permStyles.select}
                  disabled={workspacesLoading}
                  style={{ width: '100%' }}
                >
                  <option value="">-- Seleccionar workspace --</option>
                  {workspaces.map((w) => (
                    <option key={w.workspaceId} value={w.workspaceId}>
                      {w.workspaceName}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Relatório *
                <select
                  required
                  value={form.reportId}
                  onChange={(e) => handleReportChange(e.target.value)}
                  className={permStyles.select}
                  disabled={!form.workspaceId || workspacesLoading}
                  style={{ width: '100%' }}
                >
                  <option value="">-- Seleccionar relatório --</option>
                  {availableReports.map((r) => (
                    <option key={r.reportId} value={r.reportId}>
                      {r.reportName}
                    </option>
                  ))}
                </select>
                {form.workspaceId && availableReports.length === 0 && !workspacesLoading && (
                  <small style={{ color: '#888' }}>Sem relatórios neste workspace.</small>
                )}
              </label>

              <label>
                RLS Role (opcional)
                {rlsRolesLoading ? (
                  <p style={{ color: '#888', margin: '4px 0' }}>A carregar roles...</p>
                ) : (
                  <select
                    value={form.rlsRole}
                    onChange={(e) => {
                      const role = e.target.value;
                      const selectedUser = users.find((u) => u.userId === selectedUserId);
                      setForm((f) => ({
                        ...f,
                        rlsRole: role,
                        rlsUsername: role ? (selectedUser?.email ?? f.rlsUsername) : '',
                      }));
                    }}
                    className={permStyles.select}
                    disabled={!form.reportId}
                    style={{ width: '100%' }}
                  >
                    <option value="">-- Sem RLS --</option>
                    {rlsRoles.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                )}
              </label>
              {form.rlsRole && (
                <label>
                  RLS Username
                  <input
                    value={form.rlsUsername}
                    onChange={(e) => setForm((f) => ({ ...f, rlsUsername: e.target.value }))}
                    placeholder="Ex: username@empresa.com"
                  />
                </label>
              )}

              <div className={permStyles.modalActions}>
                <button
                  type="button"
                  className={permStyles.cancelBtn}
                  onClick={() => { setShowModal(false); setForm(EMPTY_FORM); }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={permStyles.saveBtn}
                  disabled={submitting || workspacesLoading || !form.reportId}
                >
                  {submitting ? 'A guardar...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
